import { Card, CardContent } from '@/components/ui/card';
import { cn } from '@/lib/utils';
import { formatBRL } from '@/features/dre/dreUtils';
import type { ChannelResultReport } from './types';

interface Props {
  report: ChannelResultReport;
}

function formatPct(value: number | null): string {
  if (value === null) return '—';
  return `${value.toFixed(0)}%`;
}

export function ChannelResultTable({ report }: Props) {
  const { channels } = report;

  if (channels.length === 0) {
    return (
      <Card>
        <CardContent className="p-12 text-center text-sm text-muted-foreground">
          Nenhuma receita ou custo com canal (centro de custo) no período.
        </CardContent>
      </Card>
    );
  }

  const netFavorable = report.totalNetProfit >= 0;

  return (
    <Card>
      <CardContent className="overflow-x-auto p-0">
        <table className="w-full min-w-[820px]">
          <thead>
            <tr className="border-b border-border bg-secondary/40 text-xs font-medium uppercase tracking-wide text-muted-foreground">
              <th className="px-4 py-3 text-left">Canal</th>
              <th className="px-4 py-3 text-right">Receita</th>
              <th className="px-4 py-3 text-right">Custos diretos</th>
              <th className="px-4 py-3 text-right">Margem contrib.</th>
              <th className="px-3 py-3 text-right">Mg%</th>
              <th className="px-4 py-3 text-right">Rateio fixo</th>
              <th className="px-4 py-3 text-right">Lucro líquido</th>
              <th className="px-3 py-3 text-right">LL%</th>
            </tr>
          </thead>

          <tbody>
            {channels.map((line) => {
              const profitable = line.netProfit >= 0;
              return (
                <tr key={line.channelId ?? '__sem_canal__'} className="border-b border-border/50">
                  <td className="px-4 py-2.5">
                    <div className="flex items-center gap-2">
                      <span
                        className="inline-block h-2.5 w-2.5 flex-shrink-0 rounded-full"
                        style={{ backgroundColor: line.color }}
                      />
                      <span className="text-sm text-foreground">{line.channelName}</span>
                      {line.salesCount > 0 && (
                        <span className="text-xs text-muted-foreground">({line.salesCount})</span>
                      )}
                    </div>
                  </td>
                  <td className="px-4 py-2.5 text-right text-sm tabular-nums text-income">
                    {formatBRL(line.revenue)}
                  </td>
                  <td className="px-4 py-2.5 text-right text-sm tabular-nums text-expense">
                    {line.directCost > 0 ? formatBRL(line.directCost) : '—'}
                  </td>
                  <td className="px-4 py-2.5 text-right text-sm font-medium tabular-nums text-foreground">
                    {formatBRL(line.contributionMargin)}
                  </td>
                  <td className="px-3 py-2.5 text-right text-xs tabular-nums text-muted-foreground">
                    {formatPct(line.contributionMarginPct)}
                  </td>
                  <td className="px-4 py-2.5 text-right text-sm tabular-nums text-expense">
                    {line.allocatedFixedCost > 0 ? formatBRL(line.allocatedFixedCost) : '—'}
                  </td>
                  <td
                    className={cn(
                      'px-4 py-2.5 text-right text-sm font-bold tabular-nums',
                      profitable ? 'text-income' : 'text-expense',
                    )}
                  >
                    {formatBRL(line.netProfit)}
                  </td>
                  <td
                    className={cn(
                      'px-3 py-2.5 text-right text-xs font-medium tabular-nums',
                      profitable ? 'text-income' : 'text-expense',
                    )}
                  >
                    {formatPct(line.netProfitPct)}
                  </td>
                </tr>
              );
            })}

            <tr
              className={cn(
                'border-t-2 border-border font-bold',
                netFavorable ? 'bg-income/10' : 'bg-expense/10',
              )}
            >
              <td className="px-4 py-3 text-sm uppercase tracking-wide text-foreground">Total</td>
              <td className="px-4 py-3 text-right text-sm tabular-nums text-income">
                {formatBRL(report.totalRevenue)}
              </td>
              <td className="px-4 py-3 text-right text-sm tabular-nums text-expense">
                {formatBRL(report.totalDirectCost)}
              </td>
              <td className="px-4 py-3 text-right text-sm tabular-nums text-foreground">
                {formatBRL(report.totalContributionMargin)}
              </td>
              <td className="px-3 py-3 text-right text-xs tabular-nums text-muted-foreground">
                {formatPct(
                  report.totalRevenue > 0
                    ? (report.totalContributionMargin / report.totalRevenue) * 100
                    : null,
                )}
              </td>
              <td className="px-4 py-3 text-right text-sm tabular-nums text-expense">
                {formatBRL(report.totalFixedCost)}
              </td>
              <td
                className={cn(
                  'px-4 py-3 text-right text-base tabular-nums',
                  netFavorable ? 'text-income' : 'text-expense',
                )}
              >
                {formatBRL(report.totalNetProfit)}
              </td>
              <td
                className={cn(
                  'px-3 py-3 text-right text-xs tabular-nums',
                  netFavorable ? 'text-income' : 'text-expense',
                )}
              >
                {formatPct(
                  report.totalRevenue > 0
                    ? (report.totalNetProfit / report.totalRevenue) * 100
                    : null,
                )}
              </td>
            </tr>
          </tbody>
        </table>
      </CardContent>
    </Card>
  );
}
